import { CantileverButton } from 'components/CantileverButton'
import { AllTagsTab } from 'features/timeSeries/components/AllTagsTab'
import { DerivedValuesTab } from 'features/timeSeries/components/DerivedValuesTab'
import { PensTab } from 'features/timeSeries/components/PensTab'
import { ResizeHandleComponent } from 'features/timeSeries/components/ResizeHandleComponent'
import { useScatterplotContext } from 'features/timeSeries/ScatterplotContext'
import {
  ModeToggleContainer,
  SectionLabel,
  TabButton,
  TabsRowContainer,
  ToggleAndLabelContainer,
} from 'features/timeSeries/sharedStyles'
import { PanelLeftClose } from 'lucide-react'
import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { BaseSensorEntry } from 'types'

import { ScatterPlotTableLegend } from './ScatterPlotTableLegend'

const StyledPanelLeftClose = styled(PanelLeftClose)`
  width: 1.25rem;
  height: 1.25rem;
`

const SidebarContainer = styled.div<{
  $width?: number
  $isCompact?: boolean
}>`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: visible;
  width: ${({ $width, $isCompact }) => {
    if ($isCompact) return '12rem'
    if ($width) return `${$width}px`
    return '40%'
  }};
  min-width: ${({ $isCompact }) => {
    if ($isCompact) return '12rem'
    return '20rem'
  }};
  background: ${({ theme }) => theme.forecast.cardBackground};
  border-right: 1px solid ${({ theme }) => theme.forecast.border};
  position: relative;
  z-index: 1;
`

const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  padding: 0.5rem;
  flex: 1;
`

const SidebarHeader = styled.header`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  padding-bottom: 0.5rem;
`

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding-top: 0.5rem;
  min-height: 0;
`

const ViewModeContainer = styled.div`
  padding-top: 1rem;
  border-top: 1px solid ${({ theme }) => theme.forecast.border};
  margin-top: auto;
  flex-shrink: 0;
`

export enum ScatterplotSidebarView {
  LEGEND = 'legend',
  PENS = 'pens',
  ALL = 'all',
  DERIVED = 'derived',
}

interface ScatterplotSidebarProps {
  tagsOptions: BaseSensorEntry[]
  selectedTags: BaseSensorEntry[]
  onClose: () => void
  setSidebarView: React.Dispatch<React.SetStateAction<ScatterplotSidebarView>>
  sidebarView: ScatterplotSidebarView
}

export const ScatterplotSidebar: React.FC<ScatterplotSidebarProps> = ({
  tagsOptions,
  selectedTags,
  onClose,
  setSidebarView,
  sidebarView,
}) => {
  const { connectPoints, setConnectPoints, showXAxis, setShowXAxis } =
    useScatterplotContext()
  const [sidebarWidth, setSidebarWidth] = useState<number | null>(null)

  const compactThreshold = 320
  const isCompact = sidebarWidth !== null && sidebarWidth < compactThreshold

  const sidebarRef = useRef<HTMLDivElement>(null)

  const handleWidthChange = (width: number | null) => {
    setSidebarWidth(width)
  }

  const renderContent = () => {
    switch (sidebarView) {
      case ScatterplotSidebarView.LEGEND:
        return (
          <ScrollableContent>
            <ScatterPlotTableLegend isCompact={isCompact} />
          </ScrollableContent>
        )
      case ScatterplotSidebarView.PENS:
        return (
          <ScrollableContent>
            <PensTab />
          </ScrollableContent>
        )
      case ScatterplotSidebarView.ALL:
        return (
          <ScrollableContent>
            <AllTagsTab tags={tagsOptions} selectedTags={selectedTags} />
          </ScrollableContent>
        )
      case ScatterplotSidebarView.DERIVED:
        return (
          <ScrollableContent>
            <DerivedValuesTab />
          </ScrollableContent>
        )
    }
  }

  return (
    <SidebarContainer
      ref={sidebarRef}
      $width={sidebarWidth || undefined}
      $isCompact={isCompact}>
      <ResizeHandleComponent
        onWidthChange={handleWidthChange}
        containerRef={sidebarRef}
      />
      <SidebarContent>
        <SidebarHeader>
          <CantileverButton iconOnly ghost onClick={onClose}>
            <StyledPanelLeftClose />
          </CantileverButton>
        </SidebarHeader>
        <TabsRowContainer>
          <TabButton
            ghost
            $isSelected={sidebarView === ScatterplotSidebarView.LEGEND}
            onClick={() => setSidebarView(ScatterplotSidebarView.LEGEND)}>
            Legend
          </TabButton>
          <TabButton
            ghost
            $isSelected={sidebarView === ScatterplotSidebarView.PENS}
            onClick={() => setSidebarView(ScatterplotSidebarView.PENS)}>
            Pens
          </TabButton>
          <TabButton
            ghost
            $isSelected={sidebarView === ScatterplotSidebarView.ALL}
            onClick={() => setSidebarView(ScatterplotSidebarView.ALL)}>
            All
          </TabButton>
          <TabButton
            ghost
            $isSelected={sidebarView === ScatterplotSidebarView.DERIVED}
            onClick={() => setSidebarView(ScatterplotSidebarView.DERIVED)}>
            Derived
          </TabButton>
        </TabsRowContainer>
        {renderContent()}
        {sidebarView === ScatterplotSidebarView.LEGEND && (
          <ViewModeContainer>
            <ToggleAndLabelContainer>
              <SectionLabel>View Mode</SectionLabel>
              <ModeToggleContainer>
                <CantileverButton
                  ghost={connectPoints}
                  onClick={() => setConnectPoints(false)}>
                  Scatter
                </CantileverButton>
                <CantileverButton
                  ghost={!connectPoints}
                  onClick={() => setConnectPoints(true)}>
                  Line
                </CantileverButton>
              </ModeToggleContainer>
            </ToggleAndLabelContainer>
            <ToggleAndLabelContainer>
              <SectionLabel>X-Axis</SectionLabel>
              <ModeToggleContainer>
                <CantileverButton
                  ghost={!showXAxis}
                  onClick={() => setShowXAxis(true)}>
                  Show
                </CantileverButton>
                <CantileverButton
                  ghost={showXAxis}
                  onClick={() => setShowXAxis(false)}>
                  Hide
                </CantileverButton>
              </ModeToggleContainer>
            </ToggleAndLabelContainer>
          </ViewModeContainer>
        )}
      </SidebarContent>
    </SidebarContainer>
  )
}
